import { unsubscribeComponent } from 'Src/ng2/shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ListNavigationService } from './../lists/services/list-navigation/list-navigation.service';
import {
  TSortDirections,
  Direction,
  SortAndFilterService,
} from 'Src/ng2/shared/services/list-services/sort-and-filter.service';
import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IStateParamsService } from 'angular-ui-router';
import { reduce } from 'lodash';
import { BehaviorSubject, Unsubscribable } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { SpinnerModal } from 'Src/app/shared/modals/status-spinner/status-spinner.service';
import { IPageHeaderMeta } from 'Src/ng2/shared/components/header/page-header/page-header-meta.interface';
import { FixedToInfiniteViewComponent } from 'Src/ng2/shared/components/list/fixed-to-infinite-view-container/fixed-to-infinite-view.component';
import {
  BackgroundJobNotificationService,
  TValidBackgroundJob,
} from 'Src/ng2/shared/services/background-job-notification-service/background-job-notification-service';
import { ObjectCache } from 'Src/ng2/shared/services/object-cache/object-cache.service';
import { IStudentGroupingsPayload } from 'Src/ng2/store/actions/student-groupings-actions/student-groupings-actions';
import { ToggleBatchActions, UpdateSelectedStudentIds } from '../../store';
import { FocusDataService } from '../mid-level-dashboard/mid-level-dash-data/focus-data/focus-data.service';
import { StudentGroupingDataService } from '../mid-level-dashboard/mid-level-dash-data/student-grouping-data/student-grouping-data.service';
import { VizMadLibService } from '../mid-level-dashboard/viz-mad-lib/viz-mad-lib.service';
import { IFocus } from '../tiles/interfaces/dashboard.interfaces';
import { IColumnStuLvl, IGroupData, IListConfig, IRowData } from './../../shared/models/list-models';

export interface ISelectedBatchActionStudentIds {
  studentId: string;
}

export interface IBatchData {
  updateAll: boolean;
  data: IRowData | IRowData[];
  level: 'SECTION' | 'ROW';
}

interface IInitializeBatchActions {
  updateStudentIds: ($event: { updateAll: boolean; studentIds: string[] }) => void;
  reset: () => void;
}
@Component({
  selector: 'student-level-dash',
  template: require('./student-level-dash.component.html'),
  styles: [require('./student-level-dash.component.scss')],
})
@unsubscribeComponent
export class StudentLevelDashComponent extends FixedToInfiniteViewComponent implements OnInit {
  @Input() schoolId: string;
  @Input() batchActionsMode$: Observable<boolean>;

  @ViewChild('entry', { read: ViewContainerRef, static: false }) entry: ViewContainerRef;
  @ViewChild('listContent', { static: false }) listContent;

  public noDataMessage: any;
  public onFixed: Function;
  public onInfinite: Function;
  public onBatchAction: Function;
  public onRowClick: Function;
  public onUiRowDataUpdate: Function = () => null;

  public dynamicColumns = false;
  public dynamicColumnIndexMap = false;

  public filterFormControl: FormControl;
  public sortKey$: BehaviorSubject<string>;
  public sortDirection$: BehaviorSubject<TSortDirections>;
  public updateSort: Function;

  public focusData$ = new BehaviorSubject(null);
  public groupingData$ = new BehaviorSubject<IGroupData[]>(null);
  public groupData$ = new BehaviorSubject<IGroupData[]>(null);
  public columns: IColumnStuLvl[];
  public pageHeaderMeta: IPageHeaderMeta;
  public selectedStudentIds: ISelectedBatchActionStudentIds[] = [];
  public sectionHeaderIsChecked: boolean = false;
  public updateBatchActions: IInitializeBatchActions;
  public batchMode$: Observable<boolean>;
  public columnIndexMap: { [key: string]: number };
  public batchActionsModeSub: Unsubscribable;
  public notificationServiceSub: Unsubscribable;
  public filterSub: Unsubscribable;
  public isFirst: boolean = true;
  public backgroundJobDependencies: TValidBackgroundJob[] = ['BulkStudentUpdate'];
  public listConfig: IListConfig;

  constructor(
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams: IStateParamsService,
    @Inject('SpinnerModal') public spinnerService: SpinnerModal,
    public notificationService: BackgroundJobNotificationService,
    private objectCache: ObjectCache,
    private vizMadlibService: VizMadLibService,
    private focusDataService: FocusDataService,
    private studentGroupingDataService: StudentGroupingDataService,
    public store: Store<any>,
    public resolver: ComponentFactoryResolver,
    public navigationService: ListNavigationService,
  ) {
    super(resolver);
  }

  ngOnInit(): void {
    this.spinnerService.activateSpinner('Loading...');

    this.filterFormControl = new FormControl(this.$stateParams.search || null);
    this.sortKey$ = new BehaviorSubject(this.$stateParams.sortKey || 'STUDENT_NAME');
    this.sortDirection$ = new BehaviorSubject<TSortDirections>(this.$stateParams.sortDirection || Direction.asc);

    this.updateSort = (sortKey: string) => {
      SortAndFilterService.updateSortCol(sortKey, this.sortKey$, this.sortDirection$);
      this.navigationService.updateUrl(this.sortKey$, this.sortDirection$, this.filterFormControl);
    };

    this.initializeStudentLevelData(this.schoolId, this.$stateParams);
    this.updateBatchActions = this.initializeBatchActions();
    this.resetBatchActions();
    this.notificationServiceSub = this.notificationService
      .getMessage()
      .pipe(
        filter(({ backgroundJob }) => this.backgroundJobDependencies.includes(backgroundJob)),
        tap(() => this.initializeStudentLevelData(this.schoolId, this.$stateParams)),
      )
      .subscribe();

    // overwrite super methods
    this.onBatchAction = this.formatAndFireBatchActionEvt;
    this.onRowClick = this.goToStudentProfile;

    this.onFixed = () => {
      this.listContent.nativeElement.classList.remove('infinite');
      this.pageHeaderMeta = { ...this.pageHeaderMeta, ...{ icon: this.getFixedHeaderIcon() } };
    };

    this.onInfinite = (instance: ComponentRef<any>) => {
      this.listContent.nativeElement.classList.add('infinite');
      this.pageHeaderMeta = { ...this.pageHeaderMeta, ...{ icon: this.getInfiniteHeaderIcon(instance) } };
    };

    this.batchActionsModeSub = this.batchActionsMode$
      .pipe(tap((mode: boolean) => (!mode ? this.resetBatchActions() : null)))
      .subscribe();

    // Update URL on filter change
    this.filterSub = this.filterFormControl.valueChanges
      .pipe(tap(() => this.navigationService.updateUrl(this.sortKey$, this.sortDirection$, this.filterFormControl)))
      .subscribe();
  }

  initializeStudentLevelData(schoolId: string, params: IStateParamsService): void {
    const { focus } = params;

    this.focusDataService
      .getFocusData$({ schoolId, focusKey: focus })
      .pipe(
        tap((focusData: IFocus) => {
          this.focusData$.next(focusData);
        }),
        switchMap((focusData: IFocus) =>
          this.studentGroupingDataService.getStudentGroupingPayload(focusData, schoolId),
        ),
        tap((payload: IStudentGroupingsPayload) => {
          this.columns = payload.columns;
          this.columnIndexMap = this.columns.reduce((mapping, col: IColumnStuLvl, i: number) => {
            mapping[col.graphQlKey] = i;
            return mapping;
          }, {});
        }),
        switchMap((payload: IStudentGroupingsPayload) =>
          this.studentGroupingDataService.getStudentGroupingData$(payload),
        ),
        tap(() => this.navigationService.updateUrl(this.sortKey$, this.sortDirection$, this.filterFormControl)),
        tap((studentGrouping: IGroupData[]) => {
          this.groupingData$.next(studentGrouping);
          if (this.isFirst) {
            this.createListContainer();
            this.pageHeaderMeta = this.getPageHeaderMeta(this.focusData$.value);
          }
          this.isFirst = false;
          this.spinnerService.deactivateSpinner();
        }),
        take(1),
      )
      .subscribe();

    this.listConfig = {
      listType: 'STUDENT',
      noDataMessage: 'No students to display',
      sortableColumns: true,
    };
  }

  getPageHeaderMeta(focusData: IFocus): IPageHeaderMeta {
    const { filterOptions, focusOptions } = focusData;
    const { focus, filter, rowStub: groupingName = '' } = this.$stateParams;
    const subtitle = {
      focusName: this.vizMadlibService.getHumanKey(focusOptions, focus),
      filterName: this.vizMadlibService.getHumanKey(filterOptions, filter),
      groupingName,
    };
    const validatedSubTitle = this.validateSubTitle(subtitle);
    const pageHeaderMeta = {
      ...this.initPageHeaderMeta(validatedSubTitle),
      ...{ icon: this.getFixedHeaderIcon() },
    };
    return pageHeaderMeta;
  }

  formatAndFireBatchActionEvt($event: { updateAll: boolean; data: IRowData[]; level: 'SECTION' | 'ROW' }): void {
    let studentIds: string[];
    const { updateAll, data, level } = $event;
    switch (level) {
      case 'SECTION':
        studentIds = data.map((row: IRowData) => this.getStudentIdFromRow(row));
        break;
      case 'ROW':
        studentIds = Array.of(this.getStudentIdFromRow(data));
        break;
      default:
        studentIds = [];
    }
    const batchData = { updateAll, studentIds };
    this.updateBatchActions.updateStudentIds(batchData);
  }

  private getStudentIdFromRow(row): string {
    const stub = row[0];
    const metaData = JSON.parse(stub.meta);
    const studentId = metaData.data;
    return studentId;
  }

  goToStudentProfile(studentTableRowData: IRowData[]): void {
    // first object in array is the student column
    const [studentCol] = studentTableRowData;
    const studentMetaData = JSON.parse(studentCol.meta);
    const cachedStudentId = this.objectCache.cacheObject({ studentId: [studentMetaData.data.slice(0, 9)] });
    this.$state.go('main.school.single-student-filter-all', { filter: cachedStudentId });
  }

  // Validation logic overview: (JCHU)
  // 1. Remove parenthesis and the repeated 'students' in filter name
  // 2. Reset grouping name if it has any same word as in filter name
  validateSubTitle({ focusName, filterName, groupingName }): IPageHeaderMeta['subTitle'] {
    filterName = this.reformFilterName(filterName);
    groupingName = this.checkRepeatedGroupingName({ filterName, groupingName });
    return { focusName, filterName, groupingName };
  }

  // Reform the filter name to fit the student level page needs (JCHU)
  reformFilterName(filterName: string): string {
    return filterName
      .split(' ')
      .filter(word => {
        const patternToRemove1 = 'students';
        const patternToRemove2 = /\(/;
        const lowercasedWord = word.toLowerCase();
        return !lowercasedWord.match(patternToRemove1) && !lowercasedWord.match(patternToRemove2);
      })
      .join(' ');
  }

  // Check whether grouping name has any same word as in filter name (JCHU)
  checkRepeatedGroupingName({ filterName, groupingName }): string {
    const lowerFilterName = filterName.toLowerCase();
    const lowerGroupingName = groupingName.toLowerCase();
    if (lowerFilterName === lowerGroupingName) {
      groupingName = '';
    } else {
      const lowerFilterWords = lowerFilterName.split(' ');
      const lowerGroupingWords = lowerGroupingName.split(' ');
      lowerGroupingWords.some(word => {
        if (lowerFilterWords.includes(word)) {
          groupingName = '';
          return true;
        }
      });
    }
    return groupingName;
  }

  initPageHeaderMeta({ focusName, filterName, groupingName }): IPageHeaderMeta {
    return {
      title: 'Student list',
      subTitle: {
        focusName,
        filterName,
        groupingName,
      },
      icon: null,
    };
  }

  getInfiniteHeaderIcon(instance: any) {
    const handler = () => (instance.aniState = 'collapsed');
    return {
      name: 'close',
      tooltip: 'Close',
      handler,
    };
  }

  getFixedHeaderIcon() {
    return {
      name: 'arrow-left',
      tooltip: 'Back to Breakdown',
      handler: () => {
        const { focus, filter, grouping } = this.$stateParams;
        this.$state.go('main.school.lists.midLevel', { focus, filter, grouping }, { location: 'replace' });
      },
    };
  }

  initializeBatchActions(): IInitializeBatchActions {
    let idHash = {};
    const batchActions = {
      updateStudentIds: ($event: { updateAll: boolean; studentIds: string[] }): void => {
        const { updateAll, studentIds } = $event;
        studentIds.map((id: string) => {
          // using _id in new lists but batch actions requires osis id
          const studentId = id.slice(0, -6);
          if (idHash[studentId] && !updateAll) delete idHash[studentId];
          else idHash[studentId] = true;
          return studentId;
        });
        const studentIdsToSend = reduce(idHash, (result, _val, key) => (result = [...result, { studentId: key }]), []);
        this.store.dispatch(new UpdateSelectedStudentIds(studentIdsToSend));
      },
      reset: (): void => {
        idHash = {};
        this.store.dispatch(new UpdateSelectedStudentIds([]));
      },
    };
    return batchActions;
  }

  resetBatchActions(): void {
    this.updateBatchActions.reset();
    this.store.dispatch(new ToggleBatchActions(false));
  }
}
